import { getActiveLook, getActiveMode } from "./activeLook";
import { mixHex } from "./contrast";
import { AdminLook, LookMode, LOOKS, LookTokens } from "./looks";

/**
 * Colours people pick and we store (staff colour, calendar overlay, the
 * service colour default). Data, not theme: the same in every look.
 */
export const STAFF_SWATCHES = [
  "#1e88e5",
  "#43a047",
  "#f4511e",
  "#8e24aa",
  "#3949ab",
  "#00897b",
  "#f6bf26",
  "#d81b60",
  "#546e7a",
] as const;

export const OVERLAY_SWATCHES = ["#1e88e5", "#8e24aa", "#00897b", "#f4511e", "#3949ab", "#c0ca33", "#d81b60", "#6d4c41"];

export const SERVICE_SWATCH_DEFAULT = "#1976d2";

export type StatusKey = "PENDING" | "CONFIRMED" | "CANCELLED" | "COMPLETED" | "NO_SHOW";
type Fill = { bg: string; border: string };

/** Colours that carry a meaning (status, calendar bands, alerts). Solid fills carry white text. */
export type DataColors = {
  status: Record<StatusKey, Fill>;
  /** A booking block with no other colour. */
  booking: string;
  /** Unknown status on the bookings calendar. */
  unknown: Fill;
  band: {
    schedule: string;
    break: string;
    pending: string;
    cancelled: string;
    completed: string;
    noShow: string;
  };
  available: string;
  unavailable: string;
  nowLine: string;
  /** Class (cohort) blocks and chips. */
  session: Fill;
  /** Done / selected marks. */
  ok: string;
  /** Impersonation banner. */
  alert: { solid: string; text: string };
  /** Sign-in error box (rendered before the theme). */
  dangerBox: { bg: string; border: string; fg: string };
  limitOver: string;
  codeBg: string;
  /** «Зураггүй» picture. */
  placeholder: { bg: string; stroke: string; text: string };
};

const CLASSIC: DataColors = {
  status: {
    PENDING: { bg: "#fb8c00", border: "#ef6c00" },
    CONFIRMED: { bg: "#1e88e5", border: "#1565c0" },
    CANCELLED: { bg: "#757575", border: "#424242" },
    COMPLETED: { bg: "#43a047", border: "#2e7d32" },
    NO_SHOW: { bg: "#9e9e9e", border: "#616161" },
  },
  booking: "#1e88e5",
  unknown: { bg: "#5c6bc0", border: "#3949ab" },
  band: {
    schedule: "#2e7d32",
    break: "#9e9e9e",
    pending: "#f9a825",
    cancelled: "#9e9e9e",
    completed: "#43a047",
    noShow: "#bdbdbd",
  },
  available: "#43a047",
  unavailable: "#e53935",
  nowLine: "#ff5252",
  session: { bg: "#00897b", border: "#00695c" },
  ok: "#4caf50",
  alert: { solid: "#dc2626", text: "#b91c1c" },
  dangerBox: { bg: "#fee2e2", border: "#fecaca", fg: "#991b1b" },
  limitOver: "#d32f2f",
  codeBg: "#f3f4f6",
  placeholder: { bg: "#1F2A40", stroke: "#4FD1C5", text: "#A3AED0" },
};

const fill = (bg: string, t: LookTokens): Fill => ({ bg, border: mixHex(bg, t.fg, 0.3) });

/** Solid fills from the look's light tokens (they carry white text in either mode); surfaces from the person's mode. */
function fromLook(solid: LookTokens, surface: LookTokens): DataColors {
  const s = solid;
  const noShow = mixHex(s.muted, s.bg, 0.3);
  return {
    status: {
      PENDING: fill(s.warnFg, s),
      CONFIRMED: fill(s.chart[0], s),
      CANCELLED: fill(s.muted, s),
      COMPLETED: fill(s.okFg, s),
      NO_SHOW: fill(noShow, s),
    },
    booking: s.chart[0],
    unknown: fill(s.chart[2], s),
    band: {
      schedule: s.okFg,
      break: s.muted,
      pending: s.warnFg,
      cancelled: s.muted,
      completed: s.okFg,
      noShow,
    },
    available: s.okFg,
    unavailable: s.dangerFg,
    nowLine: s.dangerFg,
    session: fill(s.chart[3], s),
    ok: s.okFg,
    alert: { solid: s.dangerFg, text: surface.dangerFg },
    dangerBox: { bg: surface.dangerBg, border: mixHex(surface.dangerBg, surface.dangerFg, 0.25), fg: surface.dangerFg },
    limitOver: surface.dangerFg,
    codeBg: surface.quietBg,
    placeholder: { bg: surface.quietBg, stroke: surface.borderStrong, text: surface.muted },
  };
}

export function dataColors(look: AdminLook = getActiveLook(), mode: LookMode = getActiveMode()): DataColors {
  return look === "classic" ? CLASSIC : fromLook(LOOKS[look].light, LOOKS[look][mode]);
}
